import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, RefreshCw, Coins, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STEPS = ['generatingStep1', 'generatingStep2', 'generatingStep3', 'generatingStep4', 'generatingStep5'];
const PHASE_TO_STEP = { outline: 0, modules: 1, media: 2, narration: 3, validation: 4 };

const ERROR_CODE_TO_KEY = {
  budget: 'course.errorDetailBudget',
  rateLimit: 'course.errorDetailRateLimit',
  timeout: 'course.errorDetailTimeout',
  parsing: 'course.errorDetailParsing',
  validation: 'course.errorDetailValidation',
  narration: 'course.errorDetailNarration',
  generic: 'course.errorDetailGeneric',
};

function PhaseLine({ progress }) {
  const { t } = useTranslation();
  if (!progress?.phase) return null;
  const { phase, completed = 0, total = 0, current_module = '' } = progress;
  let text = '';
  if (phase === 'outline') text = t('course.phaseOutline');
  else if (phase === 'modules') text = t('course.phaseModules', { current: Math.min(completed + 1, total || completed + 1), total, title: current_module });
  else if (phase === 'media') text = t('course.phaseMedia');
  else if (phase === 'narration') text = t('course.phaseNarration');
  else if (phase === 'validation') text = t('course.phaseValidation');
  const pct = phase === 'modules' && total ? Math.round((completed / total) * 100) : null;
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-left" data-testid="generation-phase">
      <p className="text-[#20c997] text-xs font-medium truncate">{text}</p>
      {pct !== null && (
        <div className="mt-2 h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-[#20c997] rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

export default function CourseGeneration() {
  const { courseId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('generating');
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState('');
  const [refunded, setRefunded] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const pollRef = useRef(null);

  const stopPolling = () => clearInterval(pollRef.current);

  const startPolling = useCallback(() => {
    stopPolling();
    setCurrentStep(0);
    setProgress(null);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/courses/${courseId}`, { withCredentials: true });
        if (data.generation_progress?.phase) {
          setProgress(data.generation_progress);
          setCurrentStep(PHASE_TO_STEP[data.generation_progress.phase] ?? 0);
        }
        if (data.status === 'ready') {
          stopPolling();
          setStatus('ready');
          setTimeout(() => navigate(`/course/${courseId}`), 1200);
        } else if (data.status === 'error') {
          stopPolling();
          setStatus('error');
          const key = ERROR_CODE_TO_KEY[data.error_code] || 'course.errorDetailGeneric';
          setError(t(key));
          setErrorDetails(data.error_message || '');
          setRefunded(data.refunded_credits || 0);
        }
      } catch (e) {
        console.error(e);
      }
    }, 3000);
  }, [courseId, navigate, t]);

  useEffect(() => {
    startPolling();
    return () => stopPolling();
  }, [startPolling]);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await axios.post(`${API}/courses/${courseId}/retry`, {}, { withCredentials: true });
      setStatus('generating');
      setError('');
      setErrorDetails('');
      startPolling();
    } catch (e) {
      setError(e.response?.data?.detail || t('course.errorDetailGeneric'));
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0e17] flex flex-col items-center justify-center px-4 py-12">
      <div className="absolute top-20 left-1/4 w-64 h-64 bg-[#ff6b6b]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-md w-full text-center">
        {status === 'error' ? (
          <div className="animate-fade-up" data-testid="generation-error">
            <AlertCircle size={48} className="text-[#ff6b6b] mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold text-white mb-2">{t('course.errorTitle')}</h2>
            <p className="text-[#e8e8e8] text-sm mb-4" data-testid="generation-error-message">{error}</p>

            <div className="flex items-center justify-center gap-2 bg-[#20c997]/10 border border-[#20c997]/30 rounded-xl px-4 py-2.5 mb-4 text-[#20c997] text-sm" data-testid="generation-refund-notice">
              <Coins size={15} />
              <span>{refunded > 0 ? t('course.errorRefunded', { n: refunded }) : t('course.errorNoCharge')}</span>
            </div>

            {errorDetails && (
              <div className="mb-6 text-left">
                <button
                  onClick={() => setShowDetails(d => !d)}
                  className="flex items-center gap-1 text-xs text-[#94a3b8] hover:text-white transition-colors mx-auto"
                  data-testid="toggle-error-details"
                >
                  {showDetails ? <ChevronUp size={12} /> : <ChevronDown size={12} />} {t('course.errorTechnicalDetails')}
                </button>
                {showDetails && (
                  <pre className="mt-2 bg-white/5 border border-white/10 rounded-xl p-3 text-[11px] text-[#94a3b8] whitespace-pre-wrap break-words max-h-48 overflow-auto" data-testid="error-technical-details">
                    {errorDetails}
                  </pre>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={handleRetry} disabled={retrying} className="btn-primary flex items-center justify-center gap-2" data-testid="retry-btn">
                <RefreshCw size={16} className={retrying ? 'animate-spin' : ''} />
                {retrying ? '...' : t('course.errorRetryBtn')}
              </button>
              <button onClick={() => navigate('/dashboard')} className="btn-secondary" data-testid="back-dashboard-btn">
                {t('course.errorBackBtn')}
              </button>
            </div>
          </div>
        ) : status === 'ready' ? (
          <div className="animate-fade-up" data-testid="generation-ready">
            <CheckCircle size={48} className="text-[#20c997] mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold text-white">{t('course.readyTitle')}</h2>
          </div>
        ) : (
          <div className="animate-fade-up space-y-8" data-testid="generation-loading">
            <div className="relative mx-auto w-24 h-24">
              <div className="absolute inset-0 rounded-full border-2 border-white/5" />
              <div className="absolute inset-0 rounded-full border-2 border-[#ff6b6b] border-t-transparent animate-spin" />
              <div className="absolute inset-3 rounded-full border border-[#20c997]/30" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="font-heading font-bold text-xl text-white">K</span>
              </div>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-bold text-white mb-2">{t('course.generating')}</h2>
              <p className="text-[#e8e8e8] text-sm">{t('course.generatingWait')}</p>
            </div>

            <PhaseLine progress={progress} />

            <div className="space-y-3 text-left">
              {STEPS.map((step, i) => (
                <div key={step} className={`flex items-center gap-3 transition-all duration-500 ${i <= currentStep ? 'opacity-100' : 'opacity-30'}`} data-testid={`generation-step-${i + 1}`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                    i < currentStep ? 'bg-[#20c997]' : i === currentStep ? 'border-2 border-[#ff6b6b] bg-[#ff6b6b]/10 animate-pulse' : 'border border-white/20'
                  }`}>
                    {i < currentStep && <CheckCircle size={12} className="text-white" />}
                  </div>
                  <span className={`text-sm font-medium ${i <= currentStep ? 'text-white' : 'text-[#e8e8e8]'}`}>{t(`course.${step}`)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
